import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { useState, type ReactElement } from 'react';
import { useRepertoireEntry, useUpdateRepertoireEntry, useRemoveFromRepertoire } from '@/lib/queries/repertoire';
import { useMasteryLevels, useQueueStatuses, useKeys, useTimeSignatures, useForms } from '@/lib/queries/reference-data';
import { useUpdateArrangement } from '@/lib/queries/pieces';
import { RepertoireEntryForm, type RepertoireEntryFormData } from '@/components/organisms/repertoire-entry-form';
import { Button } from '@/components/atoms/button';

const GENERIC_SAVE_FAILURE = 'Please try again. If the problem persists, refresh the page.';

type SaveError =
  | { kind: 'full'; message: string }
  | { kind: 'shared-failed-personal-saved'; message: string }
  | { kind: 'personal-failed-shared-saved'; message: string };

function describeError(error: unknown): string {
  if (error instanceof Error && error.message.trim() !== '') return error.message;
  return GENERIC_SAVE_FAILURE;
}

type Settled<T> = { ok: true; value?: T } | { ok: false; error: unknown };

async function settle<T>(run: () => Promise<T>): Promise<Settled<T>> {
  try {
    const value = await run();
    return { ok: true, value };
  } catch (error: unknown) {
    return { ok: false, error };
  }
}

function classifySaveResult(
  sharedResult: Settled<unknown>,
  personalResult: Settled<unknown>,
  sharedDirty: boolean,
  personalDirty: boolean,
): SaveError | null {
  if (sharedResult.ok && personalResult.ok) return null;

  if (!sharedResult.ok && personalResult.ok && personalDirty) {
    return { kind: 'shared-failed-personal-saved', message: describeError(sharedResult.error) };
  }

  if (!personalResult.ok && sharedResult.ok && sharedDirty) {
    return { kind: 'personal-failed-shared-saved', message: describeError(personalResult.error) };
  }

  const cause = !sharedResult.ok ? sharedResult.error : (personalResult as { ok: false; error: unknown }).error;
  return { kind: 'full', message: describeError(cause) };
}

// Filename uses `$arrangementId_` (trailing underscore) so TanStack Router treats
// this as a sibling of /repertoire/:arrangementId, not a nested child — the detail
// route renders no <Outlet />. Removing the underscore re-introduces QA finding #2.
export const Route = createFileRoute('/repertoire_/$arrangementId_/edit')({
  component: EditRepertoireEntryPage,
});

function isSharedDirty(data: RepertoireEntryFormData, entry: { keyId: string | null; timeSignatureId: string | null; formId: string | null; arrangementName: string }): boolean {
  return data.keyId !== entry.keyId
    || data.timeSignatureId !== entry.timeSignatureId
    || data.formId !== entry.formId
    || data.prettyName !== entry.arrangementName;
}

function isPersonalDirty(data: RepertoireEntryFormData, entry: { masteryLevelId: string | null; queueStatusId: string; practiceTempoBeats: number | null; performanceTempoBeats: number | null; notes: string | null }): boolean {
  return data.masteryLevelId !== entry.masteryLevelId
    || data.queueStatusId !== entry.queueStatusId
    || data.practiceTempoBeats !== entry.practiceTempoBeats
    || data.performanceTempoBeats !== entry.performanceTempoBeats
    || data.notes !== entry.notes;
}

function EditRepertoireEntryPage(): ReactElement {
  const { arrangementId } = Route.useParams();
  const navigate = useNavigate();
  const { data: entry, isLoading, isError } = useRepertoireEntry(arrangementId);
  const { data: masteryData } = useMasteryLevels();
  const { data: queueData } = useQueueStatuses();
  const { data: keyData } = useKeys();
  const { data: timeSignatureData } = useTimeSignatures();
  const { data: formData } = useForms();
  const updateEntry = useUpdateRepertoireEntry();
  const updateArrangement = useUpdateArrangement();
  const removeFromRepertoire = useRemoveFromRepertoire();
  const [saveError, setSaveError] = useState<SaveError | null>(null);

  const handleSave = async (data: RepertoireEntryFormData): Promise<void> => {
    if (!entry) return;
    setSaveError(null);

    const sharedDirty = isSharedDirty(data, entry);
    const personalDirty = isPersonalDirty(data, entry);

    const sharedResult = sharedDirty
      ? await settle(() =>
          updateArrangement.mutateAsync({
            arrangementId,
            prettyName: data.prettyName,
            keyId: data.keyId,
            timeSignatureId: data.timeSignatureId,
            formId: data.formId,
          }),
        )
      : { ok: true as const };

    const personalResult = personalDirty
      ? await settle(() =>
          updateEntry.mutateAsync({
            arrangementId,
            masteryLevelId: data.masteryLevelId ?? undefined,
            queueStatusId: data.queueStatusId,
            practiceTempoBeats: data.practiceTempoBeats,
            performanceTempoBeats: data.performanceTempoBeats,
            notes: data.notes,
          }),
        )
      : { ok: true as const };

    setSaveError(classifySaveResult(sharedResult, personalResult, sharedDirty, personalDirty));

    if (sharedResult.ok && personalResult.ok) {
      void navigate({ to: '/repertoire/$arrangementId', params: { arrangementId } });
    }
  };

  const handleCancel = (): void => {
    void navigate({ to: '/repertoire/$arrangementId', params: { arrangementId } });
  };

  const handleRemove = async (): Promise<void> => {
    await removeFromRepertoire.mutateAsync(arrangementId);
    void navigate({ to: '/repertoire' });
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--bg-page)] text-sm text-[var(--ink-3)]">
        Loading…
      </div>
    );
  }

  if (isError || !entry) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-[var(--bg-page)] p-6 text-center">
        <p className="font-display text-xl font-semibold text-[var(--ink)]">Arrangement not found</p>
        <Link to="/repertoire">
          <Button variant="secondary" size="sm">
            Back to repertoire
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg-page)] pb-24 text-[var(--ink)]">
      <header className="border-b border-[var(--rule)] bg-[var(--bg-surface)] px-6 py-6 lg:px-10">
        <div className="mx-auto flex max-w-3xl items-center">
          <Link
            to="/repertoire/$arrangementId"
            params={{ arrangementId }}
            className="inline-flex items-center gap-2 rounded-[var(--r-sm)] border border-[var(--rule)] bg-[var(--bg-soft)] px-3 h-10 text-xs font-bold text-[var(--ink)] transition-colors hover:border-[var(--brand)]"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Link>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-6 py-10 lg:px-10">
        {saveError !== null && (
          <div
            role="alert"
            className="mb-6 rounded-[var(--r-sm)] border border-[var(--error,red)] bg-[var(--error-wash,#fdecec)] p-4 text-sm text-[var(--error,red)]"
          >
            {saveError.kind === 'full' && (
              <>
                <p className="font-bold">Save failed</p>
                <p className="mt-1">{saveError.message}</p>
              </>
            )}
            {saveError.kind === 'shared-failed-personal-saved' && (
              <>
                <p className="font-bold">Personal settings saved, but arrangement metadata save failed</p>
                <p className="mt-1">{saveError.message}</p>
              </>
            )}
            {saveError.kind === 'personal-failed-shared-saved' && (
              <>
                <p className="font-bold">Arrangement metadata saved, but personal settings save failed</p>
                <p className="mt-1">{saveError.message}</p>
              </>
            )}
          </div>
        )}
        <RepertoireEntryForm
          mode="edit"
          entry={entry}
          masteryLevels={masteryData?.content ?? []}
          queueStatuses={queueData?.content ?? []}
          keys={keyData?.content ?? []}
          timeSignatures={timeSignatureData?.content ?? []}
          forms={formData?.content ?? []}
          onSave={(data) => {
            void handleSave(data);
          }}
          onCancel={handleCancel}
          onRemove={handleRemove}
          isSaving={updateEntry.isPending || updateArrangement.isPending || removeFromRepertoire.isPending}
        />
      </main>
    </div>
  );
}
